import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { EllipsisHorizontalIcon } from '@heroicons/react/24/outline';
import cx from 'classnames';

import { ActionMenu } from '../../ui/ActionMenu';
import { Avatar } from '../../ui/Avatar';
import { PillButton } from '../../ui/PillButton';

import { useCommunityMembership } from '../../../_hooks/useCommunityMembership';
import { useMemberMenu } from '../../../_hooks/useMemberMenu';

interface SidebarUserProps {
  /** Called when the user taps a profile link — used to close the mobile drawer overlay. */
  onNavigate?: () => void;
}

export const SidebarUser: FC<SidebarUserProps> = ({ onNavigate }) => {
  const { t } = useTranslation();
  const { openSignInModal } = useCommunityMembership();
  const { isLoggedIn, displayName, avatarUrl, menuItems } = useMemberMenu(onNavigate);

  if (!isLoggedIn) {
    return (
      <div className="flex h-12 items-center rounded-full w-full">
        <PillButton
          variant="primary"
          onClick={() => openSignInModal()}
          className="inline-flex w-full px-6 py-2 transition-opacity hover:opacity-90"
        >
          {t('shared.cta.sign_in')}
        </PillButton>
      </div>
    );
  }

  const userPillContent = (
    <>
      <Avatar src={avatarUrl} alt={displayName} className="size-6 shrink-0" fallbackClassName="bg-gray-300" />
      <span className="min-w-0 flex-1 truncate text-left text-[15px] font-medium leading-5 tracking-[-0.13px] md:text-[13px] md:leading-4">
        {displayName}
      </span>
    </>
  );

  const pillClassName =
    'flex items-center gap-2.5 h-10 p-2 rounded-full bg-community-text-on-bg/[0.08] text-community-text-on-bg w-full';

  return (
    <div className="relative">
      <ActionMenu
        className="w-full"
        triggerLabel={t('community.join.member_menu')}
        triggerClassName={cx(pillClassName, 'text-left transition-opacity hover:opacity-90')}
        trigger={
          <>
            {userPillContent}
            <EllipsisHorizontalIcon className="size-4 shrink-0 text-community-text-on-bg opacity-70" />
          </>
        }
        placement="top-end"
        items={menuItems}
      />
    </div>
  );
};
